import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import type { UserRole } from '@/contexts/RoleContext';
import { isAdmin } from '@/contexts/RoleContext';

export const SETTER_PERMISSION_KEYS = [
  'setter.home',
  'setter.leads',
  'setter.settings',
] as const;

export const CLOSER_PERMISSION_KEYS = [
  'closer.home',
  'closer.fathom',
  'closer.demo_library',
  'closer.settings',
] as const;

export const ALL_TEAM_PERMISSION_KEYS = [
  ...SETTER_PERMISSION_KEYS,
  ...CLOSER_PERMISSION_KEYS,
] as const;

export type TeamPermissionKey = typeof ALL_TEAM_PERMISSION_KEYS[number];

// Zod enum constrains admin-UI writes: only these literal keys can be persisted.
// Blocks T-16-07 permission_key tampering at the client boundary.
export const teamPermissionKeySchema = z.enum(ALL_TEAM_PERMISSION_KEYS);

export const DEFAULT_TEAM_PERMISSIONS: Record<'setter' | 'closer', readonly TeamPermissionKey[]> = {
  setter: SETTER_PERMISSION_KEYS,
  closer: CLOSER_PERMISSION_KEYS,
};

/**
 * PERMISSION_META — single source of truth for human-readable permission
 * labels rendered in TeamPermissionsSection. Keyed by TeamPermissionKey so
 * future additions land alongside their copy in one PR.
 *
 * Copy intentionally matches 16.3-CONTEXT.md decisions block verbatim.
 */
export const PERMISSION_META: Record<TeamPermissionKey, { title: string; description: string }> = {
  'setter.home':         { title: 'View Setter Home',     description: 'Allows this setter to access their personal home page with KPIs (booked-count, show-rate, attribution).' },
  'setter.leads':        { title: 'View Setter Leads',    description: 'Allows this setter to view the leads they have been assigned in GoHighLevel and their booking status.' },
  'setter.settings':     { title: 'Setter Settings',      description: 'Allows this setter to access their profile settings (display name, notification preferences).' },
  'closer.home':         { title: 'View Closer Home',     description: 'Allows this closer to access their personal home page with assigned-clients KPIs (demos this week, show-rate, deal value).' },
  'closer.fathom':       { title: 'Connect Fathom',       description: 'Allows this closer to self-connect their Fathom account so demo recordings auto-import. Without this, the closer must rely on an admin to connect Fathom for them.' },
  'closer.demo_library': { title: 'View Demo Library',    description: 'Allows this closer to view their library of completed demo recordings, including AI summaries and outcomes.' },
  'closer.settings':     { title: 'Closer Settings',      description: 'Allows this closer to access their profile settings (display name, notification preferences, payment payout info).' },
};

interface TeamPermissionsResult {
  isAllowed: (key: TeamPermissionKey) => boolean;
  raw: Array<{ permission_key: string; enabled: boolean }>;
}

/**
 * useTeamPermissions
 *
 * Fetches the current user's team_member_permissions rows and returns an
 * `isAllowed(key)` helper. Admins short-circuit to allow-all without a DB hit.
 *
 * Expected to be called once per session (React Query caches by [user_id, role]).
 */
export function useTeamPermissions(userId: string | null, role: UserRole) {
  return useQuery<TeamPermissionsResult>({
    queryKey: ['team_permissions', userId, role],
    enabled: !!userId,
    staleTime: 60_000,
    queryFn: async () => {
      // Admin short-circuit — always allow, never hit the DB.
      if (isAdmin(role)) {
        return { isAllowed: () => true, raw: [] };
      }
      const { data, error } = await supabase
        .from('team_member_permissions')
        .select('permission_key, enabled')
        .eq('user_id', userId!);
      if (error) throw error;
      const map = new Map<string, boolean>();
      for (const row of (data ?? []) as Array<{ permission_key: string; enabled: boolean }>) {
        map.set(row.permission_key, row.enabled);
      }
      return {
        isAllowed: (key: TeamPermissionKey) => map.get(key) ?? false,
        raw: (data ?? []) as Array<{ permission_key: string; enabled: boolean }>,
      };
    },
  });
}
